// ======================================================================
// 📁 RoutingCreateOverlay.jsx
// ======================================================================

import React, { useEffect, useState } from "react";
import {
  useNavigate,
  useParams,
  useLocation,
} from "react-router-dom";
import axiosClient from "../../../../../api/axiosClient";

import RoutingHeaderForm from "./RoutingHeaderForm";
import RoutingOperationsTable from "./RoutingOperationsTable";

export default function RoutingCreateOverlay() {
  const navigate = useNavigate();
  const location = useLocation();
  const { routingId } = useParams();

  // ------------------------------------------------------------
  // MODE DETECTION
  // ------------------------------------------------------------
  const isView = location.pathname.includes("/view/");
  const isEdit = Boolean(routingId) && !isView;

  const mode = isView
    ? "view"
    : isEdit
    ? "edit"
    : "create";

  // ------------------------------------------------------------
  // STATE
  // ------------------------------------------------------------
  const [header, setHeader] = useState({
    routing_name: "",
    product_family_id: "",
    equipment_id: "",   // NEW
    routing_type: "STANDARD",
  });

  const [operations, setOperations] = useState([]);
  const [approvalStatus, setApprovalStatus] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  // ------------------------------------------------------------
  // Close overlay
  // ------------------------------------------------------------
  const closeOverlay = () => {
    navigate("/ppe/routing");
  };

  // ------------------------------------------------------------
  // LOAD ROUTING (EDIT / VIEW MODE)
  // ------------------------------------------------------------
  useEffect(() => {
    if (!routingId) return;

    setLoading(true);

    axiosClient
      .get(`/api/ppe/routing/routings/${routingId}`)
      .then((res) => {
        const data = res.data?.data;
        if (!data) return;

        setHeader({
          routing_name: data.routing_name,
          product_family_id: data.product_family_id,
          equipment_id: data.equipment_id || "",   // NEW
          routing_type: data.routing_type,
        });

        setApprovalStatus(data.approval_status || "");

        setOperations(
          (data.operations || []).map((op) => ({
            seq: op.operation_seq_no,
            operation_name: op.operation_name,
            process_id: op.process_id,
            predecessor_seq:
              op.predecessor_operation_seq || "",
            setup_time_minutes:
              op.setup_time_minutes || 0,
            run_time_minutes:
              op.run_time_minutes || 0,
            scrap_percent:
              op.scrap_percent || 0,
            machine_groups:
              op.machine_groups || [],
          }))
        );
      })
      .catch((err) => {
        console.error("Failed to load routing", err);
        alert("Failed to load routing");
        navigate("/ppe/routing");
      })
      .finally(() => setLoading(false));
  }, [routingId, navigate]);

  // ------------------------------------------------------------
  // SAVE (CREATE / UPDATE) – DISABLED IN VIEW MODE
  // ------------------------------------------------------------
  const saveDraft = async () => {
    if (mode === "view") return;

    if (!header.routing_name || !header.product_family_id) {
      alert("Routing name and Product Family are required");
      return;
    }

    if (operations.length === 0) {
      alert("At least one operation is required");
      return;
    }

    const payload = {
    ...header,

    product_family_id: Number(header.product_family_id),

    equipment_id: header.equipment_id
      ? Number(header.equipment_id)
      : null,

    operations: operations.map((op) => ({
      operation_seq_no: op.seq,
      operation_code: `OP-${op.seq}`,
      operation_name: op.operation_name,
      process_id: op.process_id,
      predecessor_seq: op.predecessor_seq
        ? Number(op.predecessor_seq)
        : null,
      setup_time_minutes:
        op.setup_time_minutes || 0,
      run_time_minutes: op.run_time_minutes,
      scrap_percent: op.scrap_percent || 0,
      machine_groups: (op.machine_groups || []).map(
        (mg, index) => ({
          machine_group_id: mg.machine_group_id,
          preference_rank:
            mg.preference_rank ?? index + 1,
          capacity_share_percent:
            mg.capacity_share_percent ?? null,
          is_primary: mg.is_primary === true,
          alternate_allowed:
            mg.alternate_allowed !== false,
        })
      ),
    })),
  };

    // --------------------------------------------------
    // 🔍 DEBUGGER
    // --------------------------------------------------
    console.group(
      isEdit
        ? "✏️ UPDATE ROUTING DRAFT"
        : "🚀 CREATE ROUTING DRAFT"
    );
    console.log("HEADER:", header);
    console.log("OPERATIONS:", operations);
    console.log("PAYLOAD:", payload);
    console.groupEnd();

    try {
      setSaving(true);

      if (isEdit) {
        await axiosClient.put(
          `/api/ppe/routing/routings/${routingId}`,
          payload
        );
        alert("Routing updated successfully");
      } else {
        await axiosClient.post(
          "/api/ppe/routing/routings",
          payload
        );
        alert("Routing draft created successfully");
      }

      navigate("/ppe/routing");
    } catch (err) {
      console.group("❌ SAVE ERROR");
      console.error(err);
      console.log("Response:", err?.response?.data);
      console.groupEnd();

      alert(
        err?.response?.data?.message ||
          "Failed to save routing"
      );
    } finally {
      setSaving(false);
    }
  };

  // ------------------------------------------------------------
  // RENDER
  // ------------------------------------------------------------
  return (
    <div
      style={{
        position: "fixed",
        top: 10,
        left: 10,
        right: 0,
        bottom: 0,
        background: "#f8fafc",
        zIndex: 200,
        overflowY: "auto",
      }}
    >
      {/* ================= HEADER ================= */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          background:
            "linear-gradient(90deg, #0f766e 0%, #14b8a6 50%, #5eead4 100%)",
          padding: "16px 20px",
          color: "#fff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <h4 style={{ margin: 0 }}>
            {mode === "view"
              ? "View Routing"
              : isEdit
              ? "Edit Routing"
              : "Create Routing"}
          </h4>

          <div
            style={{
              fontSize: 13,
              opacity: 0.9,
              display: "flex",
              gap: 10,
              alignItems: "center",
            }}
          >
            <span>
              Manufacturing routing definition
            </span>

            {approvalStatus && (
              <span
                className="badge bg-dark"
                style={{ fontSize: 11 }}
              >
                {approvalStatus}
              </span>
            )}
          </div>
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <button
            className="btn btn-light"
            onClick={closeOverlay}
          >
            {mode === "view" ? "Close" : "Cancel"}
          </button>

          {mode !== "view" && (
            <button
              className="btn btn-dark"
              onClick={saveDraft}
              disabled={saving || loading}
            >
              {saving
                ? "Saving..."
                : isEdit
                ? "Update Draft"
                : "Save Draft"}
            </button>
          )}
        </div>
      </div>

      {/* ================= BODY ================= */}
      <div style={{ padding: 20 }}>
        <RoutingHeaderForm
          value={header}
          onChange={setHeader}
          disabled={mode === "view"}
        />

        <RoutingOperationsTable
          value={operations}
          onChange={setOperations}
          readOnly={mode === "view"}
        />
      </div>
    </div>
  );
}
